import { Component, Input, signal, computed, inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { ProductService } from '../../../core/services/listing/product.service';
import { PublicListingService } from '../../../core/services/listing/public-listing.service';

@Component({
  selector: 'app-product-ratings',
  standalone: true,
  imports: [CommonModule, FormsModule],
  templateUrl: './product-ratings.component.html',
  styleUrls: ['./product-ratings.component.scss'],
})
export class ProductRatingsComponent {
  @Input() productId!: number;
  @Input() set reviews(val: any[]) {
    this._reviews.set(val || []);
  }

  private productService = inject(ProductService);
  private publicListingService = inject(PublicListingService);

  _reviews = signal<any[]>([]);
  get reviews() {
    return this._reviews();
  }

  // New Rating State
  userRating = signal<number>(5);
  userName = '';
  userEmail = '';
  userPhone = '';
  userComments = '';

  isSubmitting = signal(false);
  submitSuccess = signal(false);

  // Derived Stats
  averageRating = computed(() => {
    const revs = this.reviews;
    if (revs.length === 0) return 0;
    const sum = revs.reduce((acc, r) => acc + (r.rating || r.stars || 0), 0);
    return sum / revs.length;
  });

  ratingStats = computed(() => {
    const revs = this.reviews;
    const total = revs.length || 1; // prevent div by zero
    const counts = { 5: 0, 4: 0, 3: 0, 2: 0, 1: 0 };
    revs.forEach((r) => {
      const s = Math.round(r.rating || r.stars || 0);
      if (s >= 1 && s <= 5) counts[s as 1 | 2 | 3 | 4 | 5]++;
    });

    return [5, 4, 3, 2, 1].map((stars) => ({
      stars,
      percentage: (counts[stars as 1 | 2 | 3 | 4 | 5] / total) * 100,
    }));
  });

  canSubmit(): boolean {
    return this.userEmail.trim().length > 0 && this.userRating() > 0 && !this.isSubmitting();
  }

  submitReview() {
    if (!this.canSubmit()) return;
    this.isSubmitting.set(true);

    const names = this.userName.trim().split(' ');
    const firstName = names[0] || '';
    const lastName = names.slice(1).join(' ') || '';

    const payload = {
      productId: Number(this.productId),
      firstName: firstName,
      lastName: lastName,
      email: this.userEmail,
      phone: this.userPhone || null,
      rating: this.userRating(),
      comments: this.userComments,
    };

    this.publicListingService.reviewProduct(this.productId, payload).subscribe({
      next: () => {
        this.isSubmitting.set(false);
        this.submitSuccess.set(true);
        this._reviews.update((curr) => [{ ...payload, reviewerName: this.userName }, ...curr]);
        this.userName = '';
        this.userEmail = '';
        this.userPhone = '';
        this.userComments = '';
        this.userRating.set(5);

        setTimeout(() => this.submitSuccess.set(false), 5000);
      },
      error: (err) => {
        console.error('[ProductRatings] Review submission failed', err);
        this.isSubmitting.set(false);
      },
    });
  }

  getStarArray(rating: number | undefined | null): number[] {
    const val = rating || 0;
    return Array.from({ length: Math.floor(val) }, (_, i) => i + 1);
  }
}
